import { usePersistedState } from "@/lib/persistedFilters";
import { useEffect, useMemo, useState } from "react";
import { CalendarDays, Check, Copy, Loader2, Search } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Avatar } from "@/components/Avatar";
import { MONTHS } from "@/lib/celebrations";
import { toast } from "sonner";

const pad2 = (v) => String(v).padStart(2, "0");
const isMissing = (r) => !r.birthday || !r.work_anniversary;
const seen = (iso) => {
  if (!iso) return "never signed in";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `last in ${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
};

/** HR-only grid — every staff member's birthday (month + day) and start date,
 *  saved inline as you change each field. Incomplete people float to the top
 *  so HR can chase them; the sign-in gate asks them itself. */
export function HumiDatesGrid({ defaultOnly = false }) {
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState("");
  const [saved, setSaved] = useState("");
  const [q, setQ] = usePersistedState("humi-dates.q", "");
  const [only, setOnly] = useState(defaultOnly);

  useEffect(() => {
    api.get("/humi/dates").then((r) => setRows(r.data.items || [])).catch(() => setRows([]));
  }, []);

  const save = async (uid, patch) => {
    setSaving(uid);
    try {
      await api.put(`/humi/dates/${uid}`, patch);
      setRows((cur) => cur.map((r) => (r.user_id === uid ? { ...r, ...patch } : r)));
      setSaved(uid);
      setTimeout(() => setSaved(""), 1800);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally {
      setSaving("");
    }
  };

  const setBirthday = (row, month, day) => {
    if (month === "none") { save(row.user_id, { birthday: "" }); return; }
    if (!month || !day) return;
    save(row.user_id, { birthday: `${pad2(month)}-${pad2(day)}` });
  };

  const incomplete = useMemo(() => (rows || []).filter(isMissing), [rows]);
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (rows || [])
      .filter((r) => (!only || isMissing(r))
        && (!needle || r.name?.toLowerCase().includes(needle) || r.email?.toLowerCase().includes(needle)))
      .sort((a, b) => (isMissing(b) ? 1 : 0) - (isMissing(a) ? 1 : 0)
        || (a.name || "").localeCompare(b.name || ""));
  }, [rows, q, only]);

  if (!rows) return null;

  const copyEmails = async () => {
    const list = incomplete.map((r) => r.email).filter(Boolean).join(", ");
    if (!list) return;
    try {
      await navigator.clipboard.writeText(list);
      toast.success(`${incomplete.length} email addresses copied`);
    } catch {
      toast.error("Could not reach the clipboard");
    }
  };

  return (
    <Card className="overflow-hidden border-slate-200 p-0 dark:border-slate-700" data-testid="humi-dates-grid">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3 dark:border-slate-800">
        <CalendarDays className="h-4 w-4 text-primary" />
        <h3 className="font-heading text-sm font-semibold text-slate-800 dark:text-slate-100">Birthdays &amp; start dates</h3>
        <span className="text-[11px] text-slate-400">HR only · saves as you type</span>
        {incomplete.length > 0 && (
          <>
            <button type="button" onClick={() => setOnly((v) => !v)} data-testid="humi-dates-missing"
              className={`ml-auto rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors ${only
                ? "bg-amber-500 text-white"
                : "bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-500/15 dark:text-amber-300"}`}>
              {incomplete.length} incomplete{only ? " · showing only" : ""}
            </button>
            <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs" onClick={copyEmails}
              data-testid="humi-dates-copy-emails">
              <Copy className="h-3 w-3" /> Copy their emails
            </Button>
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-2 dark:border-slate-800">
        <div className="relative w-56">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a person"
            className="h-8 pl-8 text-xs" data-testid="humi-dates-search" />
        </div>
        <p className="text-[11px] text-slate-400">
          {incomplete.length === 0
            ? "Everyone is on file."
            : "Anyone missing a date is asked for it the next time they sign in — this list is for chasing the ones who don't."}
        </p>
      </div>

      <ul className="divide-y divide-slate-100 dark:divide-slate-800">
        {shown.length === 0 && (
          <li className="px-4 py-8 text-center text-xs text-slate-400" data-testid="humi-dates-empty">Nobody matches that.</li>
        )}
        {shown.map((r) => {
          const [bm, bd] = (r.birthday || "").split("-");
          return (
            <li key={r.user_id} data-testid={`humi-dates-row-${r.user_id}`}
                className={`flex flex-wrap items-center gap-3 px-4 py-2.5 ${isMissing(r) ? "bg-amber-50/40 dark:bg-amber-500/5" : ""}`}>
              <Avatar name={r.name} src={r.avatar_url} className="h-8 w-8 shrink-0" />
              <div className="min-w-[160px] flex-1">
                <div className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">{r.name}</div>
                <div className="truncate text-[11px] text-slate-400">
                  {r.team || "—"}
                  {isMissing(r) && (
                    <span className="ml-1.5 text-amber-600 dark:text-amber-400" data-testid={`humi-dates-gap-${r.user_id}`}>
                      · {!r.birthday && !r.work_anniversary ? "both missing" : !r.birthday ? "no birthday" : "no start date"} · {seen(r.last_login)}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <Select value={bm || "none"} onValueChange={(v) => setBirthday(r, v, bd || "1")}>
                  <SelectTrigger className="h-8 w-[104px] text-xs" data-testid={`humi-dates-bmonth-${r.user_id}`}>
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No birthday</SelectItem>
                    {MONTHS.map((m, i) => <SelectItem key={m} value={pad2(i + 1)}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input className="h-8 w-[58px] text-xs" placeholder="Day"
                       inputMode="numeric" defaultValue={bd || ""}
                       data-testid={`humi-dates-bday-${r.user_id}`}
                       disabled={!bm}
                       onBlur={(e) => {
                         const day = e.target.value.replace(/\D/g, "").slice(0, 2);
                         if (bm && day && day !== bd) setBirthday(r, bm, day);
                       }} />
              </div>
              <Input type="date" className="h-8 w-[152px] text-xs"
                     defaultValue={r.work_anniversary || ""}
                     data-testid={`humi-dates-start-${r.user_id}`}
                     onChange={(e) => {
                       const v = e.target.value;
                       if (v !== (r.work_anniversary || "")) save(r.user_id, { work_anniversary: v });
                     }} />
              <span className="w-4 shrink-0">
                {saving === r.user_id && <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />}
                {saved === r.user_id && <Check className="h-3.5 w-3.5 text-emerald-500" data-testid={`humi-dates-saved-${r.user_id}`} />}
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

export default HumiDatesGrid;
